/**
 * Global search wire types (Story V10-K) — the standalone tenant-wide search feature.
 *
 * ⚠️ This is NOT the Brok agent. These types back a plain search bar + `/search`
 * endpoint (hybrid keyword + semantic). Imported by apps/api (the search module)
 * and apps/web (the SearchBar). apps/ai does NOT import this (source-tree rule).
 *
 * A SearchHit is deliberately flat + display-oriented: the api resolves the title/
 * subtitle/snippet + the client `route` server-side so the frontend just renders +
 * navigates (coding-standards §3 — no domain logic in the UI).
 */

/**
 * The searchable top-level entity types surfaced by global search.
 *
 * German domain terms (Kunde/Police/Dokument/Kommunikation) — kept as the real
 * concept, matching the UI (coding-standards §1). ASCII identifiers.
 *   - 'kunde'         → a person or firma (Kunden/Personen/Firmen)
 *   - 'police'        → a policy (matched on Policennummer / carrier UID)
 *   - 'dokument'      → a document (semantic match over its chunks; keyword on filename)
 *   - 'kommunikation' → a communication (email/etc — subject + from/to)
 *   - 'aufgabe'       → a task/Pendenz (matched on its title). Wave 5 (C1): the requirement
 *                       lists Tasks among the things org-wide search must cover, and nothing
 *                       searched them at all.
 */
export const SEARCH_HIT_TYPES = ['kunde', 'police', 'dokument', 'kommunikation', 'aufgabe'] as const;

/** Union of the searchable entity types. */
export type SearchHitType = (typeof SEARCH_HIT_TYPES)[number];

/**
 * How a hit was found:
 *   - 'keyword'  → exact/trigram/ILIKE match on a natural identifier (number, name, UID, email).
 *   - 'semantic' → vector (bge-m3) similarity over document chunks.
 * Keyword hits rank above semantic hits (exact Policennummer wins over a fuzzy match).
 */
export const SEARCH_MATCH_KINDS = ['keyword', 'semantic'] as const;

/** Union of the two match kinds. */
export type SearchMatchKind = (typeof SEARCH_MATCH_KINDS)[number];

/**
 * One unified search result row (merged + ranked across the keyword + semantic legs).
 *
 * `route` is the client navigation target (e.g. '/policen/<id>') resolved server-side
 * so the SearchBar simply renders a <Link to={route}>. `score` is a normalized
 * [0, 1]-ish relevance used only for ranking (higher = better); it is NOT a domain value.
 */
export interface SearchHit {
  /** The entity type of this hit. */
  type: SearchHitType;
  /** The entity id (person/firma/policy/document/communication uuid). */
  id: string;
  /** Primary display line (e.g. the customer name, policy number, filename, email subject). */
  title: string;
  /** Optional secondary line (e.g. city, carrier UID, sender). */
  subtitle: string | null;
  /** Optional short excerpt (semantic hits carry the matched chunk text). */
  snippet: string | null;
  /** Relevance score for ranking (higher = better). Keyword hits outrank semantic hits. */
  score: number;
  /** Whether the hit came from the keyword leg or the semantic leg. */
  matchKind: SearchMatchKind;
  /** Client navigation target for click-through (e.g. '/customers/<id>'). */
  route: string;
}

/**
 * The GET/POST /search response envelope.
 *
 * `counts` gives the per-type hit count (for the grouped dropdown headers) — every
 * SearchHitType is present (0 when no hits of that type).
 */
export interface SearchResponseDto {
  /** The (normalized) query that was searched. */
  q: string;
  /** Merged + ranked hits, capped at the effective limit. */
  items: SearchHit[];
  /** Per-type hit counts (all four types present). */
  counts: Record<SearchHitType, number>;
  /**
   * S4 — semantic-availability signal. Whether the SEMANTIC leg ran for this request (the
   * query embedding was produced). `false` = the embed service (apps/ai bge-m3) was
   * unreachable / degraded, so ONLY the keyword legs ran and the UI can say so instead of
   * silently returning less. Absent = not attempted / not applicable (blank query, mock data).
   */
  semantic_available?: boolean;
}

/** Default number of merged hits returned when no limit is given. */
export const SEARCH_DEFAULT_LIMIT = 20;

/** Hard cap on the number of merged hits. */
export const SEARCH_MAX_LIMIT = 50;
